import React from "react";

import { Text } from "src/components";
import { DynamicForm } from "src/components/Form";
import { ssnField, dobField } from "src/banking/authentication/sharedFields";
import { currencyConstraints } from "src/constraints";

import { contactFields, addressFields } from "./fields";

const sectionStyle = {
  mb: 3,
};

const timeConstraints = {
  numericality: {
    onlyInteger: true,
  },
};

const fields = ({ loanTypes }) => ({ t }) => {
  return [
    {
      type: "section",
      title: t("forms.loanApplication.loanInformation", {
        defaultValue: "What type of loan are you looking for?",
      }),
      style: sectionStyle,
      fields: [
        {
          key: "loanType",
          label: t("forms.loanApplication.loanType", {
            defaultValue: "Loan type",
          }),
          type: "dropdown",
          required: true,
          inputProps: {
            suppressLabel: true,
            options: loanTypes.map(loanType => {
              return {
                value: loanType.value,
                label: t(
                  `forms.loanApplication.laonTypeLabel.${loanType.value}`,
                  {
                    defaultValue: loanType.label,
                  }
                ),
              };
            }),
          },
        },
        {
          key: "amount",
          label: t("forms.loanApplication.amount", {
            defaultValue: "How much?",
          }),
          type: "currency",
          required: true,
          placeholder: t("forms.loanApplication.amountPlaceholder", {
            defaultValue: "Amount",
          }),
          constraints: {
            ...currencyConstraints,
          },
        },
      ],
    },
    {
      type: "section",
      title: t("forms.loanApplication.contactInformation", {
        defaultValue: "What's the best way to get in touch?",
      }),
      style: sectionStyle,
      fields: contactFields({
        t,
        includePhoneType: true,
        includeAccountNumber: false,
        phoneRequired: true,
        emailRequired: true,
      }),
    },
    {
      type: "section",
      title: t("forms.loanApplication.creditInformation", {
        defaultValue: "Personal information",
      }),
      style: sectionStyle,
      fields: [
        [
          ssnField({
            required: true,
            t,
            inputProps: {
              labelTop: true,
              renderErrorBelow: true,
            },
          }),
          dobField({
            required: true,
            t,
            label: t("forms.loanApplication.dob", {
              defaultValue: "Date of Birth",
            }),
            inputProps: {
              labelTop: true,
              renderErrorBelow: true,
            },
          }),
        ],
      ],
    },
    {
      type: "section",
      title: t("forms.loanApplication.housing", {
        defaultValue: "Housing",
      }),
      style: sectionStyle,
      renderBelowSectionTitle: () => (
        <Text mb={2}>
          {t("forms.loanApplication.housingDescription", {
            defaultValue: "Where do you currently live?",
          })}
        </Text>
      ),
      fields: [
        ...addressFields({ t }),
        {
          type: "content",
          title: "Time at Residence",
          render: () => (
            <Text>
              {t("forms.loanApplication.timeAtResidenceDescription", {
                defaultValue:
                  "How long have you lived at your current place of residence?",
              })}
            </Text>
          ),
        },
        [
          {
            key: "timeAtResidenceYears",
            required: true,
            label: t("forms.loanApplication.years", {
              defaultValue: "Years",
            }),
            type: "numeric",
            constraints: timeConstraints,
          },
          {
            key: "timeAtResidenceMonths",
            required: true,
            label: t("forms.loanApplication.months", {
              defaultValue: "Months",
            }),
            type: "numeric",
            constraints: timeConstraints,
          },
        ],
        {
          type: "content",
          title: t("forms.loanApplication.housingCostsTitle", {
            defaultValue: "Housing costs",
          }),
          render: () => (
            <Text>
              {t("forms.loanApplication.housingCostsDescription", {
                defaultValue:
                  "How much do you spend on your mortgage or rent each month?",
              })}
            </Text>
          ),
        },
        {
          key: "monthlyHousingCost",
          label: t("forms.loanApplication.monthlyHousingCost", {
            defaultValue: "Monthly Rent or Mortgage Amount",
          }),
          type: "currency",
          required: true,
          placeholder: t(
            "forms.loanApplication.monthlyHousingCostPlaceholder",
            {
              defaultValue: "Monthly Rent or Mortgage Amount",
            }
          ),
          constraints: {
            ...currencyConstraints,
          },
        },
      ],
    },
    {
      type: "section",
      title: t("forms.loanApplication.employment", {
        defaultValue: "Employment",
      }),
      style: sectionStyle,
      renderBelowSectionTitle: () => (
        <Text mb={2}>
          {t("forms.loanApplication.employmentDescription", {
            defaultValue: "What is your current primary source of income?",
          })}
        </Text>
      ),
      fields: [
        {
          key: "employerName",
          required: true,
          label: t("forms.loanApplication.employerName", {
            defaultValue: "Employer Name",
          }),
        },
        {
          key: "jobTitle",
          required: true,
          label: t("forms.loanApplication.jobTitle", {
            defaultValue: "Job Title",
          }),
        },
        {
          type: "content",
          title: t("forms.loanApplication.employmentLengthTitle", {
            defaultValue: "Time at employer",
          }),
          render: () => (
            <Text>
              {t("forms.loanApplication.employmentLengthDescription", {
                defaultValue: "How long have you worked at this employer?",
              })}
            </Text>
          ),
        },
        [
          {
            key: "employmentYears",
            required: true,
            label: t("forms.loanApplication.years", {
              defaultValue: "Years",
            }),
            type: "numeric",
            constraints: timeConstraints,
          },
          {
            key: "employmentMonths",
            required: true,
            label: t("forms.loanApplication.months", {
              defaultValue: "Months",
            }),
            type: "numeric",
            constraints: timeConstraints,
          },
        ],
        {
          type: "content",
          title: t("forms.loanApplication.monthlyIncomeTitle", {
            defaultValue: "Monthly income",
          }),
          render: () => (
            <Text>
              {t("forms.loanApplication.monthlyIncomeDescription", {
                defaultValue: "How much do you make per month?",
              })}
            </Text>
          ),
        },
        {
          key: "monthlyIncome",
          label: t("forms.loanApplication.monthlyIncome", {
            defaultValue: "Monthly Income",
          }),
          type: "currency",
          required: true,
          placeholder: t("forms.loanApplication.monthlyIncomePlaceholder", {
            defaultValue: "Monthly Income",
          }),
          constraints: {
            ...currencyConstraints,
          },
        },
      ],
    },
    {
      type: "section",
      title: t("forms.loanApplication.commentsSectionTitle", {
        defaultValue: "Comments",
      }),
      style: sectionStyle,
      fields: [
        {
          key: "comments",
          label: t("forms.loanApplication.commentsLabel", {
            defaultValue: "Is there anything else you'd like us to know?",
          }),
          type: "textarea",
          inputProps: {
            minRows: 4,
          },
        },
      ],
    },
  ];
};

const LoanApplicationForm = ({ moduleKey, options }) => {
  return (
    <DynamicForm
      formKey={moduleKey}
      fields={fields(options)}
      defaultSuccessMessageKey="forms.defaultSuccessMessage"
      needsCaptcha={false}
    />
  );
};

export default LoanApplicationForm;



// WEBPACK FOOTER //
// ./src/components/Content/forms/LoanApplicationForm.js